<script setup>
import { onMounted, computed, watch, ref } from "vue";
import { useRouter } from "vue-router";
import moment from "moment";

import Filters from "@/components/employability/filters/Filters.vue";

import debounce from "lodash/debounce";
import { useEmployabilityStore } from "@/stores/employability";

const employabilityStore = useEmployabilityStore();

const router = useRouter();

const data = computed(() => employabilityStore.overview);

const params = computed(() => employabilityStore.requestQueryParams);

const pagination = computed(() => employabilityStore.pagination);

const isFetching = ref(false);

const formatDate = (date) => {
  if (date === null) {
    return date;
  }
  return moment(date, "YYYY-MM-DD").format("DD-MM-YYYY");
};

const getEmployability = async () => {
  isFetching.value = true;

  params.value.set("page", pagination.value.currentPage - 1);
  params.value.set("size", pagination.value.pageSize);

  await employabilityStore.getEmployabilityOverview(params.value);

  isFetching.value = false;
};

const download = ref(false);

const exportTable = async () => {
  download.value = true;
  const resp = await employabilityStore.downloadExcel(params.value);

  const url = window.URL.createObjectURL(resp.data);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "employabilities.xlsx");
  document.body.appendChild(link);
  link.click();
  link.remove();
  download.value = false;
};

const checkClickEvent = (event) => {
  const check = event.target && event.target.classList.contains("mdi-pencil");
  return check;
};

const edit = (item, event) => {
  const check = checkClickEvent(event);
  if (check) {
    router.push({
      path: `/employability/edit/${item}`,
    });
  }
};

const employabilityDebounced = debounce(getEmployability, 500);

watch(
  pagination,
  () => {
    if (!isFetching.value) {
      getEmployability();
    }
  },
  {
    deep: true,
  }
);

watch(
  params,
  () => {
    employabilityDebounced();
  },
  {
    deep: true,
  }
);

const headers = [
  {
    title: "Actions",
    key: "actions",
    align: "start",
    width: "50px",
    sortable: false,
    fixed: true,
  },
  { title: "Date", key: "date" },
  { title: "Title", key: "title", width: "300px" },
  { title: "Beneficiary", key: "beneficiary", width: "200px" },
  { title: "Email", key: "beneficiaryEmail" },
  { title: "Telephone", key: "beneficiaryTelephone" },
  { title: "Odyssea Branch", key: "branch" },
  { title: "Responsible", key: "responsible" },
  { title: "Type of Service", key: "service" },
  { title: "Courses Enrolled", key: "courses" },
  { title: "Has a CV", key: "hasCV" },
  { title: "Interested to relocate", key: "relocate" },
  { title: "Languages", key: "languages" },
  { title: "Has driving license", key: "hasDrivingLicense" },
  { title: "Driving license", key: "drivingLicense" },
  { title: "Odyssea Project", key: "projects" },
];

// onBeforeMount(() => {
//   employabilityStore.resetAction();
// });

onMounted(() => {
  getEmployability();
});
</script>

<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <h1 class="mb-10 page-title">Employability Overview</h1>
        <Filters />
        <div class="d-flex justify-space-between align-center mb-5">
          <h3>Total: {{ pagination.totalItems }}</h3>
          <v-btn
            color="employability"
            :loading="download"
            :disabled="download"
            @click="exportTable()"
          >
            <div class="d-flex align-center">
              <v-icon>mdi-file-excel</v-icon>
              <span>Export</span>
            </div>
          </v-btn>
        </div>
        <v-data-table
          :items="data"
          :headers="headers"
          :items-per-page="pagination.pageSize"
          :hide-default-footer="true"
          :loading="isFetching"
          class="employability-table bordered-columns"
        >
          <template #item.title="{ item }">
            <router-link
              :to="'/employability/view/' + item.id"
              target="_blank"
              class="chip-link-color-employability"
            >
              <v-chip> {{ item.title }} </v-chip>
            </router-link>
          </template>
          <template #item.date="{ item }">
            <span style="white-space: nowrap">{{ formatDate(item.date) }}</span>
          </template>

          <template #item.beneficiary="{ item }">
            <router-link
              :to="'/beneficiary/intake/view/' + item.beneficiaryId"
              target="_blank"
              class="chip-link-color"
            >
              <v-chip> {{ item.beneficiary }} </v-chip>
            </router-link>
          </template>

          <template #item.courses="{ item }">
            <div v-for="course in item.courses" :key="course.id" class="mb-1">
              <router-link
                :to="'/academy/view/' + course.id"
                target="_blank"
                class="chip-link-color-academy"
              >
                <v-chip> {{ course.cycle }} </v-chip>
              </router-link>
            </div>
          </template>

          <template #item.actions="{ item }">
            <div class="d-flex ga-2 justify-center">
              <v-btn
                icon="mdi-pencil"
                size="x-small"
                color="employability"
                @click="edit(item.id, $event)"
              />
            </div>
          </template>
        </v-data-table>
        <div class="mt-5">
          <v-pagination
            v-model="pagination.currentPage"
            :length="pagination.pageCount"
            :total-visible="5"
          />
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="scss" scoped>
h1 {
  color: rgb(var(--v-theme-employability));
}
.v-table {
  table-layout: fixed;

  td,
  th {
    border-right: thin solid
      rgba(var(--v-border-color), var(--v-border-opacity));
    white-space: normal;
  }
}
</style>
